// POS → Restaurant → Reportes (/pos/rest-reportes).
// · Por MESERO: cuentas, comensales, documentos, ventas, 10% de servicio y
//   líneas anuladas (control de voids) — lo que pide el cierre del restaurante.
// · COCINA: tiempos por estación (promedio, percentil 90, máximo y cuántas
//   líneas pasaron del umbral rojo del KDS).
import { useCallback, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { BarChart3 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { SelectorLocal, Campo, fieldCls, money, posRpc } from './shared';
import { fechaLocal } from './shared';
import { SortTh, useSort } from '@/features/control-bancario/shared';
import { ExportarBotones } from '@/lib/utils/exportarTabla';
import type { Celda } from '@/lib/utils/exportarTabla';
import { useLocalRestaurant } from './restaurante';

interface MeseroRep {
  id: string; mesero: string; cuentas: number; comensales: number;
  documentos: number; ventas: number; servicio: number; anuladas: number;
}
interface CocinaRep {
  estacion: string; lineas: number; prom_min: number | null;
  p90_min: number | null; max_min: number | null; sobre_rojo: number;
}

export function ReportesRestaurant() {
  const { locationId, setLocationId, locations } = useLocalRestaurant();
  const [tab, setTab] = useState<'meseros' | 'cocina'>('meseros');
  const [desde, setDesde] = useState(fechaLocal());
  const [hasta, setHasta] = useState(fechaLocal());

  const { data: meseros = [] } = useQuery<MeseroRep[]>({
    queryKey: ['rest-rep-meseros', locationId, desde, hasta],
    queryFn: () => posRpc<MeseroRep[]>('rest_reporte_meseros', { _location: locationId, _desde: desde, _hasta: hasta }),
    enabled: !!locationId && tab === 'meseros',
    staleTime: 0, refetchOnMount: 'always',
  });
  const { data: cocina = [] } = useQuery<CocinaRep[]>({
    queryKey: ['rest-rep-cocina', locationId, desde, hasta],
    queryFn: () => posRpc<CocinaRep[]>('rest_reporte_cocina', { _location: locationId, _desde: desde, _hasta: hasta }),
    enabled: !!locationId && tab === 'cocina',
    staleTime: 0, refetchOnMount: 'always',
  });

  const getMesero = useCallback((r: MeseroRep, k: string): string | number | null => {
    switch (k) {
      case 'mesero': return r.mesero;
      case 'cuentas': return Number(r.cuentas);
      case 'comensales': return Number(r.comensales);
      case 'documentos': return Number(r.documentos);
      case 'ventas': return Number(r.ventas);
      case 'servicio': return Number(r.servicio);
      case 'anuladas': return Number(r.anuladas);
      default: return null;
    }
  }, []);
  const sortM = useSort(meseros, getMesero);

  const headM = ['N°', 'Mesero', 'Cuentas', 'Comensales', 'Documentos', 'Ventas', 'Servicio 10%', 'Líneas anuladas'];
  const filasM: Celda[][] = sortM.sorted.map((r, i) => [
    i + 1, r.mesero, Number(r.cuentas), Number(r.comensales), Number(r.documentos),
    Number(r.ventas), Number(r.servicio), Number(r.anuladas),
  ]);

  return (
    <div className="p-6 space-y-4 max-w-5xl">
      <div className="flex items-center gap-3 flex-wrap">
        <h1 className="text-xl font-semibold flex items-center gap-2"><BarChart3 className="w-5 h-5" /> Reportes Restaurant</h1>
        <SelectorLocal locationId={locationId} setLocationId={setLocationId} locations={locations} />
        <div className="flex rounded-md border overflow-hidden text-sm">
          <button className={`px-3 py-1 ${tab === 'meseros' ? 'bg-primary text-primary-foreground' : ''}`}
                  onClick={() => setTab('meseros')}>Por mesero</button>
          <button className={`px-3 py-1 ${tab === 'cocina' ? 'bg-primary text-primary-foreground' : ''}`}
                  onClick={() => setTab('cocina')}>Tiempos de cocina</button>
        </div>
        <Campo label=""><input className={fieldCls} type="date" value={desde} onChange={(e) => setDesde(e.target.value)} /></Campo>
        <Campo label=""><input className={fieldCls} type="date" value={hasta} onChange={(e) => setHasta(e.target.value)} /></Campo>
      </div>

      {tab === 'meseros' && (
        <Card>
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base">Ventas y servicio por mesero</CardTitle>
              <ExportarBotones nombre="restaurant-meseros" titulo="Restaurant — por mesero"
                subtitulo={`${desde} a ${hasta}`} headers={headM} filas={filasM}
                colsDerecha={[0, 2, 3, 4, 5, 6, 7]}
                fmtPDF={(v, col) => (col === 5 || col === 6) ? money(Number(v)) : String(v ?? '')} />
            </div>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-muted-foreground border-b">
                    <th className="py-2 pr-3 text-right w-10">N°</th>
                    <SortTh col="mesero" sort={sortM}>Mesero</SortTh>
                    <SortTh col="cuentas" sort={sortM} align="right">Cuentas</SortTh>
                    <SortTh col="comensales" sort={sortM} align="right">Comensales</SortTh>
                    <SortTh col="documentos" sort={sortM} align="right">Docs.</SortTh>
                    <SortTh col="ventas" sort={sortM} align="right">Ventas</SortTh>
                    <SortTh col="servicio" sort={sortM} align="right">Servicio 10%</SortTh>
                    <SortTh col="anuladas" sort={sortM} align="right">Anuladas</SortTh>
                  </tr>
                </thead>
                <tbody>
                  {sortM.sorted.map((r, i) => (
                    <tr key={r.id} className="border-b last:border-0">
                      <td className="py-2 pr-3 text-right text-muted-foreground">{i + 1}</td>
                      <td className="py-2 pr-3 font-medium">{r.mesero}</td>
                      <td className="py-2 pr-3 text-right">{r.cuentas}</td>
                      <td className="py-2 pr-3 text-right">{r.comensales}</td>
                      <td className="py-2 pr-3 text-right">{r.documentos}</td>
                      <td className="py-2 pr-3 text-right font-mono tabular-nums">{money(r.ventas)}</td>
                      <td className="py-2 pr-3 text-right font-mono tabular-nums">{money(r.servicio)}</td>
                      <td className={`py-2 pr-3 text-right ${Number(r.anuladas) > 0 ? 'text-red-600 font-medium' : ''}`}>{r.anuladas}</td>
                    </tr>
                  ))}
                  {!sortM.sorted.length && (
                    <tr><td colSpan={8} className="py-6 text-center text-muted-foreground">Sin cuentas en el rango.</td></tr>
                  )}
                  {sortM.sorted.length > 0 && (
                    <tr className="border-t-2 font-semibold bg-muted/30">
                      <td className="py-2 pr-3" colSpan={2}>Totales ({sortM.sorted.length})</td>
                      <td className="py-2 pr-3 text-right">{sortM.sorted.reduce((a, r) => a + Number(r.cuentas), 0)}</td>
                      <td className="py-2 pr-3 text-right">{sortM.sorted.reduce((a, r) => a + Number(r.comensales), 0)}</td>
                      <td className="py-2 pr-3 text-right">{sortM.sorted.reduce((a, r) => a + Number(r.documentos), 0)}</td>
                      <td className="py-2 pr-3 text-right font-mono tabular-nums">{money(sortM.sorted.reduce((a, r) => a + Number(r.ventas), 0))}</td>
                      <td className="py-2 pr-3 text-right font-mono tabular-nums">{money(sortM.sorted.reduce((a, r) => a + Number(r.servicio), 0))}</td>
                      <td className="py-2 pr-3 text-right">{sortM.sorted.reduce((a, r) => a + Number(r.anuladas), 0)}</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            <p className="text-xs text-muted-foreground border-t pt-2 mt-2">
              «Servicio 10%» es la base del reparto mensual entre trabajadores. «Anuladas» son las líneas
              comandadas anuladas con supervisor en cuentas de ese mesero (control de voids).
            </p>
          </CardContent>
        </Card>
      )}

      {tab === 'cocina' && (
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-base">Tiempos de cocina por estación (comanda → listo)</CardTitle></CardHeader>
          <CardContent>
            <table className="w-full text-sm">
              <thead>
                <tr className="text-muted-foreground border-b">
                  <th className="py-2 pr-3 text-right w-10">N°</th>
                  <th className="py-2 pr-3 text-left">Estación</th>
                  <th className="py-2 pr-3 text-right">Líneas</th>
                  <th className="py-2 pr-3 text-right">Promedio (min)</th>
                  <th className="py-2 pr-3 text-right">P90 (min)</th>
                  <th className="py-2 pr-3 text-right">Máximo (min)</th>
                  <th className="py-2 pr-3 text-right">Sobre rojo</th>
                </tr>
              </thead>
              <tbody>
                {cocina.map((r, i) => (
                  <tr key={r.estacion} className="border-b last:border-0">
                    <td className="py-2 pr-3 text-right text-muted-foreground">{i + 1}</td>
                    <td className="py-2 pr-3 font-medium">{r.estacion}</td>
                    <td className="py-2 pr-3 text-right">{r.lineas}</td>
                    <td className="py-2 pr-3 text-right font-mono">{r.prom_min ?? '—'}</td>
                    <td className="py-2 pr-3 text-right font-mono">{r.p90_min ?? '—'}</td>
                    <td className="py-2 pr-3 text-right font-mono">{r.max_min ?? '—'}</td>
                    <td className={`py-2 pr-3 text-right ${Number(r.sobre_rojo) > 0 ? 'text-red-600 font-medium' : ''}`}>{r.sobre_rojo}</td>
                  </tr>
                ))}
                {!cocina.length && (
                  <tr><td colSpan={7} className="py-6 text-center text-muted-foreground">
                    Sin líneas despachadas en el rango (se mide de «enviada» a «lista»).
                  </td></tr>
                )}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
